import React from 'react';
import { getTheme } from '../utils/theme';

const theme = getTheme();

const ShieldIcon = () => (
  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke={theme.colors.primary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const PrivacyCenter = () => {
  return (
    <div style={S.container}>
      <div style={S.hero}>
        <div style={S.shieldWrap}>
          <ShieldIcon />
          <div style={S.shieldPulse} />
        </div>
        <h1 style={S.title}>Privacy & Data Deletion & Protection Policy</h1>
        <p style={S.subtitle}>Your privacy is our highest priority. Learn how we keep your data safe.</p>
        <div style={S.badge}>🔒 Safe • Anonymous • Ephemeral</div>
      </div>

      <div style={S.grid}>
        {/* Deletion Card */}
        <div style={S.card}>
          <div style={S.cardEmoji}>🗑️</div>
          <h2 style={S.cardTitle}>Automatic Data Delete</h2>
          <p style={S.cardText}>
            To keep your chats private, we **permanently delete** everything. Once deleted, nobody can ever see it again—not even us.
          </p>
          <ul style={S.simpleList}>
            <li>✅ All chat messages & history</li>
            <li>✅ Photos & videos sent in chat</li>
            <li>✅ Voice messages & files</li>
          </ul>
          <div style={S.tag}>⏰ Deleted Daily or Weekly</div>
        </div>

        {/* Persistence Card */}
        <div style={S.card}>
          <div style={S.cardEmoji}>👤</div>
          <h2 style={S.cardTitle}>What is not Deleted?</h2>
          <p style={S.cardText}>
            We only keep the basics so you can stay connected with your friends:
          </p>
          <ul style={S.simpleList}>
            <li>✅ Your Account & Settings</li>
            <li>✅ Your Friends List</li>
            <li>✅ Your Bio & Profile Name</li>
          </ul>
          <p style={S.smallNote}>This stays safe until you delete your account.</p>
        </div>
      </div>

      <div style={S.statsBox}>
        <div style={S.stat}>
          <span style={S.statVal}>100%</span>
          <span style={S.statLabel}>Private & Secure</span>
        </div>
      </div>

      <div style={S.footerCard}>
        <div style={S.footerEmoji}>🛡️</div>
        <h3>Our Promise</h3>
        <p>
          What happens in the chat, stays in the chat. We use automatic deletion cycles
          to make sure your history isn't saved forever. (Deleted Daily or Weekly).
        </p>
        <div style={S.footerBadge}>Trusted by Millions</div>
      </div>
    </div>
  );
};

const S = {
  container: {
    padding: '60px 20px',
    maxWidth: '900px',
    margin: '0 auto',
    color: '#e2e8f0',
    height: '100%',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    fontFamily: "'Inter', sans-serif"
  },
  hero: {
    textAlign: 'center',
    marginBottom: '60px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  shieldWrap: {
    position: 'relative',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  shieldPulse: {
    position: 'absolute',
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    background: `rgba(${theme.colors.primaryRGB}, 0.1)`,
    zIndex: -1,
    animation: 'pulse 3s infinite'
  },
  title: {
    fontSize: 'clamp(32px, 7vw, 48px)',
    fontWeight: 900,
    margin: '0 0 12px',
    color: '#fff'
  },
  subtitle: {
    fontSize: '18px',
    color: '#94a3b8',
    marginBottom: '20px',
    lineHeight: 1.5
  },
  badge: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    padding: '6px 16px',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: 600,
    color: theme.colors.primary
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    marginBottom: '40px'
  },
  card: {
    background: '#111118',
    borderRadius: '28px',
    padding: '32px',
    border: '1px solid rgba(255,255,255,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  cardEmoji: { fontSize: '44px' },
  cardTitle: { fontSize: '22px', fontWeight: 800, margin: 0, color: '#fff' },
  cardText: { fontSize: '15px', color: '#94a3b8', lineHeight: 1.6, margin: 0 },
  simpleList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '14px', color: '#cbd5e1' },
  tag: {
    background: 'rgba(99,102,241,0.1)',
    color: theme.colors.primary,
    padding: '8px 12px',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: 700,
    alignSelf: 'flex-start',
    marginTop: '8px'
  },
  smallNote: { fontSize: '12px', color: '#4ade80', fontWeight: 500, margin: 0 },

  statsBox: {
    display: 'flex',
    gap: '20px',
    marginBottom: '40px',
    background: 'rgba(255,255,255,0.02)',
    padding: '30px',
    borderRadius: '28px',
    justifyContent: 'space-around',
    textAlign: 'center'
  },
  stat: { flex: 1 },
  statVal: { display: 'block', fontSize: '32px', fontWeight: 900, color: theme.colors.primary },
  statLabel: { fontSize: '12px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' },

  footerCard: {
    background: theme.colors.gradient,
    borderRadius: '32px',
    padding: '40px 30px',
    textAlign: 'center',
    color: '#fff',
    boxShadow: `0 15px 40px rgba(${theme.colors.primaryRGB}, 0.2)`
  },
  footerEmoji: { fontSize: '50px', marginBottom: '16px' },
  footerBadge: {
    display: 'inline-block',
    background: 'rgba(255,255,255,0.2)',
    padding: '5px 14px',
    borderRadius: '100px',
    fontSize: '11px',
    fontWeight: 700,
    marginTop: '20px'
  }
};

export default PrivacyCenter;
